import { Theme, useTheme } from "@mui/material";
import moment, { Moment } from "moment";
import React from "react";
import WeekDayHeader from "../WeekDayHeader";

// Style objects for style prop
const calendarTableStyle = {
    width: "100%",
    margin: 0,
    padding: 0,
    borderSpacing: "0px",
    borderCollapse: "collapse" as "collapse", // TypeScript expects this type
};
const calendarCellStyle = (theme: Theme) => ({
    color: theme.palette.text.primary,
    margin: 0,
    padding: 0,
    cursor: "pointer",
});
const calendarInnerStyle = {
    width: "34px",
    height: "34px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
};
const todayStyle = (theme: Theme) => ({
    backgroundColor: theme.palette.lightgray.main,
    borderRadius: "18px",
});
const selectedStartDateStyle = (theme: Theme) => ({
    backgroundColor: theme.palette.blue.background,
    borderBottomLeftRadius: "18px",
    borderTopLeftRadius: "18px",
    color: theme.palette.white.main,
});
const selectedEndDateStyle = (theme: Theme) => ({
    backgroundColor: theme.palette.blue.background,
    color: theme.palette.white.main,
    borderBottomRightRadius: "18px",
    borderTopRightRadius: "18px",
});
const interveningDayStyle = (theme: Theme) => ({
    backgroundColor: theme.palette.blue.background,
});
const singleDaySelectedStyle = (theme: Theme) => ({
    backgroundColor: theme.palette.blue.main,
    borderRadius: "18px",
    color: theme.palette.white.main,
});
const selectedBackgroundStyle = (theme: Theme) => ({
    borderRadius: "18px",
    backgroundColor: theme.palette.blue.main,
    padding: "4px",
});

type CalDayInterface = {
    style: any; // style object for style prop
    text: string;
    style2: any; // style object for span style prop
};

interface CalRowProps {
    row: Array<CalDayInterface>;
}

interface CalBodyProps {
    month: number;
    year: number;
    dayClick: (dayInfo: moment.Moment) => void;
    selectedStartDate?: moment.Moment;
    selectedEndDate?: moment.Moment;
    choosingBegin?: boolean;
    hoverDay?: Moment;
    setHoverDay: (any) => void;
    isRangeSelect?: boolean;
}
export default function CalendarBodyDouble(props: CalBodyProps) {
    const theme = useTheme();

    const mo: Moment = moment().year(props.year).month(props.month);
    const firstDayOfMonth: number = Number(mo.startOf("month").format("d"));
    const daysInMonth: number = mo.daysInMonth();

    const getBlanks = React.useMemo(() => {
        let blanks = Array<CalDayInterface>();
        for (let i = 0; i < firstDayOfMonth; i++) {
            blanks.push({ style: {}, text: "", style2: {} });
        }
        return blanks;
    }, [firstDayOfMonth]);

    const getCalendarDays = () => {
        let calDays = Array<CalDayInterface>();
        const currentDay: Moment = moment();

        for (let d = 1; d <= daysInMonth; d++) {
            let style = {};
            let style2 = {};
            const momentOfCurrentCalendarDay = moment(mo).date(d);

            // highlight today
            if (
                momentOfCurrentCalendarDay?.format("MM-DD-YYYY") ===
                currentDay.format("MM-DD-YYYY")
            ) {
                style = todayStyle(theme);
            }

            // highlight intervening on hover
            if (!props.choosingBegin) {
                if (
                    props.hoverDay &&
                    props.selectedStartDate &&
                    props.selectedStartDate < momentOfCurrentCalendarDay &&
                    momentOfCurrentCalendarDay < props.hoverDay
                ) {
                    style = interveningDayStyle(theme);
                }
            }
            // highlight intervening on load
            if (props.choosingBegin) {
                if (
                    props.selectedStartDate &&
                    props.selectedEndDate &&
                    momentOfCurrentCalendarDay > props.selectedStartDate &&
                    momentOfCurrentCalendarDay < props.selectedEndDate
                ) {
                    style = interveningDayStyle(theme);
                }
            }

            // highlight hovered day
            if (
                momentOfCurrentCalendarDay.format("MM-DD-YYYY") ===
                props.hoverDay?.format("MM-DD-YYYY")
            ) {
                if (props.choosingBegin) {
                    style = selectedStartDateStyle(theme);
                    style2 = selectedBackgroundStyle(theme);
                } else {
                    style = selectedEndDateStyle(theme);
                    style2 = selectedBackgroundStyle(theme);
                }
                if (!props.isRangeSelect) {
                    style = singleDaySelectedStyle(theme);
                }
            }
            // highlight start
            if (
                momentOfCurrentCalendarDay.format("MM-DD-YYYY") ===
                props.selectedStartDate?.format("MM-DD-YYYY")
            ) {
                style = selectedStartDateStyle(theme);
                style2 = selectedBackgroundStyle(theme);
            }
            // highlight end
            if (
                props.choosingBegin &&
                momentOfCurrentCalendarDay.format("MM-DD-YYYY") ===
                    props.selectedEndDate?.format("MM-DD-YYYY")
            ) {
                style = selectedEndDateStyle(theme);
                style2 = selectedBackgroundStyle(theme);
            }

            // handle edge case when dates are same day
            if (
                props.choosingBegin &&
                momentOfCurrentCalendarDay.format("MM-DD-YYYY") ===
                    props.selectedEndDate?.format("MM-DD-YYYY") &&
                momentOfCurrentCalendarDay.format("MM-DD-YYYY") ===
                    props.selectedStartDate?.format("MM-DD-YYYY")
            ) {
                style = singleDaySelectedStyle(theme);
            }

            calDays.push({ style, text: d.toString(), style2 });
        }
        return calDays;
    };

    let totalSlots = [...getBlanks, ...getCalendarDays()];
    let rows = Array<any>();
    let cells = Array<any>();
    totalSlots.forEach((day, i) => {
        if (i === 0 || i % 7 !== 0) {
            cells.push(day);
        } else {
            rows.push(cells);
            cells = new Array<any>();
            cells.push(day);
        }
        if (i === totalSlots.length - 1) {
            rows.push(cells);
        }
    });

    function onDayClick(obj) {
        let m = moment()
            .year(obj.year)
            .month(obj.month)
            .date(obj.day)
            .set("hour", 0)
            .set("minute", 0)
            .set("seconds", 0);
        props.dayClick(m);
    }

    function CalendarCell(parms: CalDayInterface) {
        const currentCellMo: Moment = moment()
            .year(props.year)
            .month(props.month)
            .date(Number(parms.text));
        return (
            <td style={calendarCellStyle(theme)}>
                <div
                    onClick={() =>
                        onDayClick({
                            month: props.month,
                            year: props.year,
                            day: Number(parms.text),
                        })
                    }
                    onMouseEnter={() => {
                        if (props.hoverDay?.date() !== currentCellMo.date()) {
                            props.setHoverDay(currentCellMo);
                        }
                    }}
                    style={{ ...parms.style, ...calendarInnerStyle }}
                >
                    <span
                        style={{
                            ...parms.style2,
                            width: "28px",
                            textAlign: "center",
                        }}
                    >
                        {parms.text}
                    </span>
                </div>
            </td>
        );
    }

    function CalendarRow(parms: CalRowProps) {
        return (
            <tr>
                {parms.row.map((cell, i) => {
                    return (
                        <CalendarCell
                            key={"rowcell" + i}
                            text={cell.text}
                            style={cell.style}
                            style2={cell.style2}
                        />
                    );
                })}
            </tr>
        );
    }

    return (
        <table style={calendarTableStyle}>
            <thead>
                <tr>
                    <WeekDayHeader />
                </tr>
            </thead>
            <tbody onMouseLeave={() => props.setHoverDay(undefined)}>
                {rows.map((d, i) => (
                    <CalendarRow key={"bodyrow" + i} row={d} />
                ))}
            </tbody>
        </table>
    );
}
